/* Fit rules come from each product's existing sizing table, never a copied price list. */
(() => {
  if (customElements.get('book-size-calculator')) return;
  class BookSizeCalculator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.inputs = [...this.querySelectorAll('[data-measure]')];
      this.result = this.querySelector('[data-result]');
      this.add = this.querySelector('[data-add]');
      this.alternatives = this.querySelector('[data-alternatives]');
      this.product = JSON.parse(this.querySelector('[data-product]').textContent);
      this.scope = document.getElementById(`MainProduct-${this.dataset.section}`);
      this.form = document.getElementById(this.dataset.form);
      this.picker = this.scope?.querySelector('variant-selects, variant-radios');
      this.submit = this.form?.querySelector('[name="add"]');
      this.rows = [...this.querySelector('[data-size-table]').content.querySelectorAll('tr')].map(row => {
        const cells = [...row.querySelectorAll('td')].map(cell => cell.textContent.trim());
        const number = value => /^\d+(?:\.\d+)?(?:\s*mm)?$/i.test(value || '') ? Number(value.replace(/\s*mm$/i, '')) : NaN;
        return { code: cells[0], height: number(cells[1]), min: number(cells[2]), max: number(cells[3]) };
      }).filter(row => row.code && row.height > 0 && row.min > 0 && row.max >= row.min);
      this.querySelector('[data-calculate]').addEventListener('click', () => this.calculate());
      this.inputs.forEach(input => {
        input.addEventListener('input', () => { input.removeAttribute('aria-invalid'); input.nextElementSibling.textContent = ''; this.invalidate(); });
        input.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); this.calculate(); } });
      });
      this.querySelector('[data-book-type]').addEventListener('change', event => {
        this.querySelector('[data-hardback]').hidden = event.target.value !== 'hardback';
        this.invalidate();
      });
      this.onPickerChange = () => { if (!this.selecting) this.invalidate('Size selection changed. Find your cover size again to confirm the fit.'); };
      this.picker?.addEventListener('change', this.onPickerChange);
      this.add.addEventListener('click', () => {
        this.syncAdd();
        if (!this.add.disabled) { window.bpTrack?.('recommended_add_clicked', { variant_id: this.recommended.id }); this.submit.click(); } // Preserve quantity, cart drawer and existing error handling.
      });
      if (this.submit) {
        this.observer = new MutationObserver(() => this.syncAdd());
        this.observer.observe(this.submit, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.picker?.removeEventListener('change', this.onPickerChange);
    }
    invalidate(message) {
      this.recommended = null;
      this.add.hidden = true;
      this.add.disabled = true;
      this.alternatives.hidden = true;
      this.result.hidden = !message;
      if (message) this.show(message);
    }
    show(message, error = false) {
      this.result.replaceChildren();
      this.result.textContent = message;
      this.result.dataset.state = error ? 'error' : 'success';
      this.result.hidden = false;
    }
    fail(message, alternatives = true) {
      window.bpTrack?.('calculator_no_result', { needs_help: alternatives });
      this.show(message, true);
      this.alternatives.hidden = !alternatives;
    }
    syncAdd() {
      const id = this.form?.querySelector('input[name="id"]');
      this.add.disabled = !this.recommended || !this.recommended.available || String(this.recommended.id) !== id?.value || !this.submit || this.submit.disabled || this.submit.getAttribute('aria-disabled') === 'true';
    }
    selectVariant(variant) {
      if (!this.picker || !this.form) return false;
      const selects = [...this.picker.querySelectorAll('select')];
      const fieldsets = [...this.picker.querySelectorAll('fieldset')];
      const controls = selects.length ? selects : fieldsets;
      if (controls.length !== variant.options.length) return false;
      // Validate every option before changing any control.
      if (selects.length && !selects.every((select, i) => [...select.options].some(option => option.value === variant.options[i]))) return false;
      const radios = fieldsets.map((fieldset, i) => [...fieldset.querySelectorAll('input[type="radio"]')].find(input => input.value === variant.options[i]));
      if (!selects.length && radios.some(input => !input)) return false;
      this.selecting = true;
      try {
        selects.forEach((select, i) => { select.value = variant.options[i]; });
        radios.forEach(input => { input.checked = true; });
        (selects[0] || radios[0]).dispatchEvent(new Event('change', { bubbles: true }));
      } finally { this.selecting = false; }
      return String(variant.id) === this.form.querySelector('input[name="id"]')?.value;
    }
    calculate() {
      window.bpTrack?.('calculator_used');
      this.invalidate();
      let invalid;
      const measurements = this.inputs.map(input => {
        const value = input.valueAsNumber;
        const valid = Number.isFinite(value) && value > 0;
        input.setAttribute('aria-invalid', String(!valid));
        input.nextElementSibling.textContent = valid ? '' : 'Enter a measurement greater than 0 mm.';
        if (!valid && !invalid) invalid = input;
        return value;
      });
      if (invalid) { this.fail('Please check the highlighted measurements.', false); invalid.focus(); return; }
      if (!this.rows.length) { this.fail('We could not read the sizing table for this product. Please contact us to check your size.'); return; }
      const allowance = this.querySelector('[data-book-type]').value === 'hardback' ? 2 : 0;
      const [height, width, spine] = measurements.map(value => value + allowance);
      const total = Math.round((2 * width + spine) * 1000000) / 1000000;
      const lowest = Math.min(...this.rows.map(row => row.height));
      const highest = Math.max(...this.rows.map(row => row.height));
      if (height < lowest || height > highest) {
        this.fail(`Your ${height} mm height${allowance ? ' including the hardback allowance' : ''} is outside this range (${lowest}–${highest} mm). Please contact us about a suitable cover.`); return;
      }
      // Preserve the original next-height rule; never choose a taller cover just to gain width.
      const nextHeight = Math.min(...this.rows.filter(row => row.height >= height).map(row => row.height));
      const heightRows = this.rows.filter(row => row.height === nextHeight);
      const fits = heightRows.filter(row => total >= row.min && total <= row.max);
      if (!fits.length) {
        const ranges = heightRows.map(row => `${row.min}–${row.max} mm`).join(' or ');
        this.fail(`Your total wraparound width is ${total} mm. At ${nextHeight} mm height, this range fits ${ranges}. Please check another width range.`); return;
      }
      const selected = this.picker ? [...this.picker.querySelectorAll('select')].map(select => select.value) : [];
      if (!selected.length && this.picker) this.picker.querySelectorAll('fieldset').forEach(fieldset => selected.push(fieldset.querySelector(':checked')?.value));
      // Match the exact size code, preserving any other selected product options.
      const matches = fits.flatMap(row => this.product.variants.filter(variant => variant.options.some((option, i) => option.trim() === row.code && variant.options.every((value, j) => j === i || value === selected[j]))).map(variant => ({ row, variant })));
      const match = matches.find(item => item.variant.available) || matches[0];
      if (!match) { this.fail('A size fits the table, but we could not match it to a purchasable option. Please contact us to confirm the size.'); return; }
      const { row, variant } = match;
      const didSelect = this.selectVariant(variant);
      if (!didSelect) { this.fail(`Size ${row.code} fits your measurements. Please select this code in the product options before adding to your basket.`, false); return; }
      this.recommended = variant;
      window.bpTrack?.('size_recommended', { variant_id: variant.id, available: variant.available });
      this.show('');
      const heading = document.createElement('strong'); heading.textContent = `Recommended size: ${row.code}`;
      const product = document.createElement('p'); product.textContent = this.product.title;
      const price = document.createElement('p'); price.textContent = `${variant.price} · ${variant.available ? 'Available to order' : 'Currently unavailable'}`;
      const detail = document.createElement('p'); detail.textContent = `Cover height ${row.height} mm · wraparound width ${row.min}–${row.max} mm. Your wraparound width: ${total} mm.${allowance ? ' Includes a 2 mm allowance on each measurement for your hardback.' : ''}`;
      const confirmation = document.createElement('p'); confirmation.textContent = variant.available ? 'This size is selected above. The basket button uses your chosen quantity.' : 'This size fits, but cannot currently be added to your basket. Contact us for availability.';
      this.result.append(heading, product, price, detail, confirmation);
      this.add.hidden = !variant.available || !this.submit;
      this.syncAdd();
    }
  }
  customElements.define('book-size-calculator', BookSizeCalculator);
})();
